import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles } from "@material-ui/core";
import { Upload } from "lucide-react";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    maxWidth: "50%",
    flexBasis: "50%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "80%",
      flexBasis: "80%",
    },
  },
  ticketActionButtons: {
    maxWidth: "50%",
    flexBasis: "50%",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const ticketCache = new Map();
const TICKET_CACHE_MAX = 20;

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [pendingMessages, setPendingMessages] = useState([]);
  const pendingRef = useRef([]);

  useEffect(() => {
    const cached = ticketCache.get(String(ticketId));
    if (cached) {
      setContact(cached.contact);
      setTicket(cached.ticket);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const fetchTicket = async () => {
      try {
        const { data } = await api.get('/tickets/' + ticketId);
        ticketCache.set(String(ticketId), { contact: data.contact, ticket: data });
        if (ticketCache.size > TICKET_CACHE_MAX) {
          ticketCache.delete(ticketCache.keys().next().value);
        }
        setContact(data.contact);
        setTicket(data);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    };
    fetchTicket();
  }, [ticketId, history]);

  useEffect(() => {
    const socket = openSocket();

    const handleTicket = (data) => {
      if (data.action === "update" && String(data.ticket?.id) === String(ticketId)) {
        setTicket(data.ticket);
        const prev = ticketCache.get(String(ticketId));
        ticketCache.set(String(ticketId), { contact: data.ticket.contact || prev?.contact, ticket: data.ticket });
      }
      if (data.action === "delete" && String(data.ticketId) === String(ticketId)) {
        toast.success("Ticket deleted sucessfully.");
        history.push("/tickets");
      }
    };
    const handleContact = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };
    socket.on("ticket", handleTicket);
    socket.on("contact", handleContact);

    return () => {
      socket.off("ticket", handleTicket);
      socket.off("contact", handleContact);
    };
  }, [ticketId, history]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleOptimisticSend = (msgs) => {
    pendingRef.current = [...pendingRef.current, ...msgs];
    setPendingMessages([...pendingRef.current]);
  };

  const handleClearOldestPending = () => {
    if (pendingRef.current.length === 0) return;
    const removed = pendingRef.current[0];
    if (removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    pendingRef.current = pendingRef.current.slice(1);
    setPendingMessages([...pendingRef.current]);
  };

  const handleDragOver = e => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = e => {
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDraggingOver(false);
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDraggingOver(false);
    if (e.dataTransfer.files.length > 0)
      setDroppedFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <div
      className={classes.root}
      id="drawer-container"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDraggingOver && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.6)",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          color: "#fff",
          pointerEvents: "none",
          border: "3px dashed rgba(255,255,255,0.5)",
          boxSizing: "border-box",
          margin: 12,
          borderRadius: 12,
        }}>
          <Upload size={48} style={{ marginBottom: 12, opacity: 0.9 }} />
          <span style={{ fontSize: "1.2rem", fontWeight: 600 }}>Drop files here</span>
        </div>
      )}
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <TicketActionButtons ticket={ticket} />
          </div>
        </TicketHeader>
        <ReplyMessageProvider>
          <MessagesList
            key={ticketId}
            ticketId={ticketId}
            isGroup={ticket.isGroup}
            pendingMessages={pendingMessages}
            onFromMeMessage={handleClearOldestPending}
          ></MessagesList>
          <MessageInput
            ticketStatus={ticket.status}
            droppedFiles={droppedFiles}
            onDropHandled={() => setDroppedFiles([])}
            onOptimisticSend={handleOptimisticSend}
          />
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
